
import { useState } from "react";
import { Heart, Bookmark, Info } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useTranslation } from "react-i18next";

interface InspirationCardProps {
  id: string;
  title: string;
  description: string;
  imageUrl: string;
  isVideo?: boolean;
  location: string;
  bestTimeToVisit?: string;
  iataCode: string;
  setIataCode: (iataCode: string) => void;
  setCityName?: (cityName: string) => void;
}

const InspirationCard = ({
  id,
  title,
  description,
  imageUrl,
  isVideo = false,
  location,
  bestTimeToVisit = "Year-round",
  iataCode,
  setIataCode,
  setCityName
}: InspirationCardProps) => {
  const [showControls, setShowControls] = useState(false);
  const [isClicked, setIsClicked] = useState(false);
  const [isInfoOpen, setIsInfoOpen] = useState(false);
  const [isLiked, setIsLiked] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const { t } = useTranslation();

  const handleImageClick = () => {
    setIsClicked(!isClicked);
  };

  return (
    <>
      <Card 
        className="group relative overflow-hidden rounded-xl hover:shadow-lg transition-shadow duration-300"
        onMouseEnter={() => setShowControls(true)}
        onMouseLeave={() => setShowControls(false)}
        style={{ cursor: "pointer" }}
        title="Click to explore activities"
        onClick={() => {
          console.debug("Card clicked for iata code:", iataCode);
          setCityName("");
          setIataCode(iataCode);
        }}
      >
        <div 
          className="cursor-pointer" 
          onClick={handleImageClick}
        >
          {isVideo ? (
            <video
              className="w-full aspect-video object-cover"
              src={imageUrl}
              loop
              muted
              playsInline
              controls={showControls}
            />
          ) : (
            <img
              src={imageUrl}
              alt={title}
              className="w-full aspect-video object-cover"
            />
          )}
        </div>
        
        <div 
          className={`absolute inset-0 bg-black/40 flex flex-col justify-between p-4 transition-all duration-300 
            ${isClicked ? 'opacity-100' : (showControls ? 'opacity-100' : 'opacity-0')}`}
        >
          <div className="text-white">
            <h3 className="text-xl font-bold">{title}</h3>
            <p className="text-sm">{location}</p>
          </div>
          
          <div className="flex justify-end gap-2">
            <Button
              variant="secondary"
              size="icon"
              onClick={() => setIsLiked(!isLiked)}
              className={`${isLiked ? 'text-red-500' : 'text-white'}`}
            >
              <Heart className="h-5 w-5" />
            </Button>
            <Button
              variant="secondary"
              size="icon"
              onClick={() => setIsSaved(!isSaved)}
              className={`${isSaved ? 'text-yellow-500' : 'text-white'}`}
            >
              <Bookmark className="h-5 w-5" />
            </Button>
            <Button
              variant="secondary"
              size="icon"
              onClick={() => setIsInfoOpen(true)}
            >
              <Info className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </Card>

      <Dialog open={isInfoOpen} onOpenChange={setIsInfoOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>
              {description}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <h4 className="font-semibold">{t("discover.inspiration.location")}</h4>
              <p className="text-sm text-muted-foreground">{location}</p>
            </div>
            <div>
              <h4 className="font-semibold">{t("discover.inspiration.bestTimeToVisit")}</h4>
              <p className="text-sm text-muted-foreground">{bestTimeToVisit}</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default InspirationCard;
